import React, { FC } from 'react';
import styled, { css } from 'styled-components/macro';

import Icon from 'components/Icon';

const Button = styled.button<{
    isLight: boolean;
}>`
    min-width: 35px;
    width: 35px;
    height: 35px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: transparent;
    cursor: pointer;
    border-radius: 17.5px;
    padding: 0px;
    outline: none;
    transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;

    &:hover {
        background-color: rgb(235, 235, 235);
    }

    ${({ isLight, theme }) =>
        isLight &&
        css`
            color: #fff;
            &:hover {
                background-color: ${theme.colors.topNavigation};
            }
        `}
`;

const Input = styled.input<{
    isLight: boolean;
}>`
    display: block;
    width: 100%;
    height: 100%;
    outline: none;
    -webkit-appearance: none;
    background-color: transparent;
    border: 0;

    &::placeholder {
        color: ${({ theme }) => theme.colors.inactive};
    }

    ${({ isLight, theme }) =>
        isLight &&
        css`
            color: #fff;
            &::placeholder {
                color: ${theme.colors.labels};
            }
        `}
`;

const Form = styled.form<{
    searchTheme: 'dark' | 'light' | 'white';
}>`
    width: 100%;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid ${({ theme }) => theme.colors.labels};
    border-radius: 20px;
    height: 40px;
    padding: 0px ${({ theme }) => theme.space.small} 0px
        ${({ theme }) => theme.space.medium};

    ${({ searchTheme }) =>
        searchTheme === 'white' &&
        css`
            background-color: #fff;
            border-width: 0;
        `}

    ${({ searchTheme }) =>
        searchTheme === 'light' &&
        css`
            border-width: 2px;
        `}
`;

export type SearchProps = {
    /** Search bar theme */
    searchTheme: 'dark' | 'light' | 'white';

    /** Object with customisation for the search input */
    input: React.InputHTMLAttributes<HTMLInputElement>;

    /** Function called when the clear button gets clicked */
    onClearClick?: React.MouseEventHandler<HTMLButtonElement>;

    /** Function called when the form gets submitted */
    onSubmitClick?: React.MouseEventHandler<HTMLButtonElement>;

    /** Boolean to indicate if the clear indicator should be visible */
    showClear?: boolean;
};

const Search: FC<SearchProps & React.HTMLAttributes<HTMLFormElement>> = ({
    searchTheme = 'dark',
    onClearClick,
    onSubmitClick,
    showClear,
    input,
    ...rest
}) => {
    let { value } = input;

    return (
        <Form searchTheme={searchTheme} {...rest}>
            <Input
                isLight={searchTheme === 'light'}
                type="text"
                value={value}
                {...input}
            />

            {showClear && value && (
                <Button
                    isLight={searchTheme === 'light'}
                    onClick={onClearClick}
                    type="reset"
                    aria-label="clear search"
                >
                    <Icon name="close" size={12} />
                </Button>
            )}

            {!showClear && (
                <Button
                    isLight={searchTheme === 'light'}
                    onClick={onSubmitClick}
                    type="submit"
                    aria-label="submit search"
                >
                    <Icon name="search" size={18} />
                </Button>
            )}
        </Form>
    );
};

export default Search;
